import { useEffect, useState } from "react";
import { useSearchParams } from 'react-router-dom';

import {
    Switch,
    Typography,
    IconButton,
} from '@mui/material';
import {
    DataGrid,
} from '@mui/x-data-grid';
import {
    Remove,
    Edit,
} from '@mui/icons-material';

import {Trans, useTranslation} from 'react-i18next';
import i18n from "../../i18n";
import api from "../../api";
import SearchBar from "../shared/SearchBar";
import EditWorkHoursDialog from "./EditWorkHoursDialog";
import DeleteFlexTimeDialog from "../shared/DeleteFlexTimeDialog";
import { computeWorkTime } from "../../model/contractInfo";
import DateFormat from "../../utils/DateFormat";


function EmployeeBaseDataPage() {
    const {t} = useTranslation()
    const [urlParam, setUrlParam] = useSearchParams({ 'search': '' });
    const [filter, setFilter] = useState(urlParam.get('search') || '');
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [workHourDialogOpen, setWorkHourDialogOpen] = useState(false);
    const [deleteFlexTimeDialogOpen, setDeleteFlexTimeDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    
    const handleOnFilter = (e) => {
        setFilter(e.target.value);
        setUrlParam({ 'search': e.target.value });
    };

    const handleOnClear = () => {
        setFilter('');
        setUrlParam('');
    };

    useEffect(() => {

        const fetchData = async () => {
            let users = await api.readUsers();
            
            for (let i = 0; i < users.length; i++){
                const location = await api.readLocation(users[i].locationId)
                
                users[i] = {
                    ...users[i],
                    location : location
                };
            }
            setUsers(users);
        };

        fetchData();
    }, []);

    useEffect(() => {
        let filtered = users.filter(u => u.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));
        setFilteredUsers(filtered);
    }, [users, filter]);

    const handleOnOpenDialog = (userId) => {
        let user = users.find(u => u.id === userId);
        setSelectedUser(user);
        setWorkHourDialogOpen(true);
    };

    const getWorkDays = (contractInfo, userId) => {
        if (!contractInfo) {
            return <Remove />
        };

        let res = '';
        let index = 0;

        for (const [key, value] of Object.entries(contractInfo)) {
            if (key !== 'weeklyWorkLoad' && key !== 'id' && value > 0) {
                if (index === 0) {
                    res = res.concat(dayMap.get(key));
                } else {
                    res = res.concat(', ' + dayMap.get(key));
                }
                index += 1;
            }
        };

        return (
            <>
                <p>{res}</p>
                <IconButton size="small" onClick={() => handleOnOpenDialog
                    (userId)}>
                    <Edit fontSize="inherit" />
                </IconButton>
            </>
        );
    };

    const dayMap = new Map(
        [
            ['mondayWorkLoad', new Date(0,0,1).toLocaleDateString(i18n.language, {weekday: 'short'})],
            ['tuesdayWorkLoad', new Date(0,0,2).toLocaleDateString(i18n.language, {weekday: 'short'})],
            ['wednesdayWorkLoad', new Date(0,0,3).toLocaleDateString(i18n.language, {weekday: 'short'})],
            ['thursdayWorkLoad', new Date(0,0,4).toLocaleDateString(i18n.language, {weekday: 'short'})],
            ['fridayWorkLoad', new Date(0,0,5).toLocaleDateString(i18n.language, {weekday: 'short'})],
            ['saturdayWorkLoad', new Date(0,0,6).toLocaleDateString(i18n.language, {weekday: 'short'})],
            ['sundayWorkLoad', new Date(0,0,7).toLocaleDateString(i18n.language, {weekday: 'short'})],
        ],
    );

    const getWeeklyHours = (contractInfo) => {
        let weeklyWorkLoad = computeWorkTime(contractInfo);
        return DateFormat.formatDuration(weeklyWorkLoad);
    }

    const handleChangeContractType = (isAllowedFlextimeAccount, userId) => {
        let user = users.find(u => u.id === userId);

        user.isAllowedFlexTimeAccount = isAllowedFlextimeAccount;

        api.updateUser(userId, user).then(res => {
            api.readUser(userId).then(res => {
                setUsers(users.map(u => {
                    if (u.id === userId) {
                        u = res;
                    };

                    return u;
                }));
            });
        });
    };

    const handleToggle = (e, userId) => {
        let user = users.find(u => u.id === userId);
        setSelectedUser(user);

        let newVal = (e.target.checked)
        if (!newVal) {
            setDeleteFlexTimeDialogOpen(true);
        } else {
            handleChangeContractType(newVal, userId);
        };
    };

    const handleUpdateUserModell = (updateUser) => {
        api.updateUser(updateUser.id, updateUser).then(res => {
            api.readUser(updateUser.id).then(res => {
                setUsers(users.map(u => {
                    if (u.id === updateUser.id) {
                        u = res;
                    };

                    return u;
                }));

                setSelectedUser(res);
            });
        });
    };

    const columns = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
        },
        {
            field: 'location',
            headerName: t('standort', {ns: 'employeeBaseDataPage', defaultValue: 'Standort'}),
            flex: 1,
            renderCell: (params) => params.row.location.name
        },
        {
            field: 'weeklyHours',
            headerName: t('wochenStunden', {ns: 'employeeBaseDataPage', defaultValue: 'Wochenstunden'}),
            flex: 1,
            renderCell: (params) => params.row.contractInfo? getWeeklyHours(params.row.contractInfo) : ''
        },
        {
            field: 'workTimeModel',
            headerName: t('arbeitszeitModell', {ns: 'employeeBaseDataPage', defaultValue: 'Arbeitszeitmodell'}),
            flex: 1,
            renderCell: (params) => getWorkDays(params.row.contractInfo, params.row.id)
        },
        {
            field: 'flexTimeAccount',
            headerName: t('arbeitszeitKonto', {ns: 'employeeBaseDataPage', defaultValue: 'Arbeitszeitkonto'}),
            flex: 1,
            renderCell: (params) => <Switch checked={params.row.isAllowedFlexTimeAccount} onChange={(e) => handleToggle(e, params.row.id)} />
        },
    ]

    return (
        <>
            <Typography variant='overline' gutterBottom><Trans i18nKey={'mitarbeiter'} ns="employeeBaseDataPage">Mitarbeiter</Trans></Typography>
            <SearchBar
                onClear={handleOnClear}
                onChange={handleOnFilter}
                value={filter}
            />
            <DataGrid
                columns={columns}
                rows={filteredUsers}
                sx={{ mt: 5 }}
            />
            <EditWorkHoursDialog
                open={workHourDialogOpen}
                onClose={() => setWorkHourDialogOpen(false)}
                user={selectedUser}
                onChange={handleUpdateUserModell}
            />
            <DeleteFlexTimeDialog
                open={deleteFlexTimeDialogOpen}
                onClose={() => setDeleteFlexTimeDialogOpen(false)}
                onSubmit={handleChangeContractType}
                userId = {selectedUser?.id}
            />
        </>
    );
};

export default EmployeeBaseDataPage
